import { getPlatform } from '@overseas/utool'

export default defineNuxtPlugin({
  name: 'falcon-plugin',
  setup: (nuxtApp) => {
    const falconBrowserVersion = '0.0.20'
    let userAgent = ''
    let isTestEnv = false

    if (import.meta.client) {
      userAgent = navigator.userAgent || ''
      const host = window.location.hostname || ''
      isTestEnv = host.includes('.t.') || host.includes('.qa.') || host.includes('localhost') || host === '127.0.0.1'
    }
    else {
      const headers = useRequestHeaders()
      userAgent = headers['user-agent'] || ''
      const host = headers.host || ''
      isTestEnv = host.includes('.t.') || host.includes('.qa.') || host.includes('localhost')
    }

    const isProd = !isTestEnv
    const platform = getPlatform(userAgent)

    const getMemberIdFromUserCookie = () => {
      try {
        const parseUserid = (val: string | null | undefined): string => {
          if (!val) {
            return ''
          }
          const decoded = decodeURIComponent(String(val))
          const match = decoded.match(/(?:^|[&;?])userid=([^&;]+)/i)
          return match ? match[1].trim() : ''
        }

        const cnUserCookie = useCookie('cnUser')?.value
        const memberIdFromCnUser = parseUserid(cnUserCookie)
        if (memberIdFromCnUser) {
          return memberIdFromCnUser
        }

        const usCookie = useCookie('us')?.value
        const memberIdFromUs = parseUserid(usCookie)
        if (memberIdFromUs) {
          return memberIdFromUs
        }

        if (typeof document !== 'undefined') {
          const cookieStr = document.cookie || ''
          const match = cookieStr.match(/(?:^|;\s*)(?:cnUser|us)=([^;]+)/i)
          if (match && match[1]) {
            return parseUserid(match[1])
          }
        }
      }
      catch {}
      return ''
    }

    const memberId = getMemberIdFromUserCookie()
    const config = useRuntimeConfig()
    const falconRelease = (config?.public?.falconRelease as string) || (typeof process !== 'undefined' ? process.env.FALCON_RELEASE : '') || '1.0.0'

    const falconConfig: Record<string, any> = {
      app: 20002,
      appid: 20002,
      appUk: 'hopegoo.web.pc',
      platform,
      version: falconRelease,
      env: isProd ? 'product' : 'stage',
      endpoint: isProd
        ? 'https://app.hopegoo.com/monitorApi/useBehaviorLog/postData'
        : 'https://app.t.hopegoo.com/monitorApi/useBehaviorLog/postData',
    }
    if (memberId) {
      falconConfig.user = { memberId }
    }

    const syncFalconUser = () => {
      if (!import.meta.client) {
        return
      }
      const Falcon = (window as any).Falcon
      const currentMemberId = getMemberIdFromUserCookie()
      if (currentMemberId && Falcon?.getSdk) {
        Falcon.getSdk()?.setUser({ memberId: currentMemberId })
      }
    }

    let appCreated = false
    let vuePluginInitialized = false

    const setupFalconVuePlugin = () => {
      syncFalconUser()
      if (!import.meta.client || !appCreated || vuePluginInitialized) {
        return
      }

      const Falcon = (window as any).Falcon
      if (!Falcon?.VuePlugin) {
        return
      }

      try {
        const vuePlugin = new Falcon.VuePlugin()
        Falcon.use(vuePlugin)
        vuePlugin.setup({
          vueApp: nuxtApp.vueApp,
          vueRouter: nuxtApp.$router,
        })
        vuePluginInitialized = true
        syncFalconUser()
      }
      catch (e) {
        console.error('Falcon plugin error', e)
      }
    }

    const falconConfigScript = `window.FalconConfig = ${JSON.stringify(falconConfig)};
try {
  var _m = (document.cookie || '').match(/(?:^|;\\s*)(?:cnUser|us)=([^;]+)/i);
  if (_m && _m[1]) {
    var _u = decodeURIComponent(_m[1]).match(/(?:^|[&;?])userid=([^&;]+)/i);
    if (_u && _u[1]) {
      window.FalconConfig.user = window.FalconConfig.user || {};
      window.FalconConfig.user.memberId = _u[1].trim();
    }
  }
} catch(e) {}`

    useHead({
      script: [
        {
          innerHTML: falconConfigScript,
          tagPosition: 'head',
        },
        {
          src: `https://static${isProd ? '' : '.t'}.hopegoo.com/falcon-browser/${falconBrowserVersion}/falcon-browser.umd.js`,
          async: true,
          tagPosition: 'bodyOpen',
          onload: setupFalconVuePlugin,
        },
      ],
    })

    if (import.meta.client) {
      nuxtApp.hook('app:created', () => {
        appCreated = true
        setupFalconVuePlugin()
      })
      nuxtApp.hook('page:finish', () => {
        syncFalconUser()
      })
    }
  },
})
